import { useEffect, useRef, useState, type ReactNode } from 'react';
import { Mail } from 'lucide-react';
import { shadeHexColor, isHexColor } from '../../utils/color';
import { TransparentImage } from '../common/TransparentImage';

/** How long the flap/letter animation runs before the content takes over. */
const OPEN_ANIMATION_MS = 1900;

/** Envelope paper color used when the campaign has not picked one. */
export const DEFAULT_ENVELOPE_COLOR = '#f4efe6';

/** True when the visitor asked their OS to reduce motion. */
export function prefersReducedMotion(): boolean {
  if (typeof window === 'undefined' || typeof window.matchMedia !== 'function') return false;
  return window.matchMedia('(prefers-reduced-motion: reduce)').matches;
}

export interface EnvelopeRevealProps {
  /** The invitation itself — rendered once the envelope has been opened. */
  children: ReactNode;
  /** Wax-seal artwork (the event logo). Falls back to an envelope glyph. */
  sealImageUrl?: string | null;
  /** Campaign accent color — themes the letter text and the hint line. */
  accentColor: string;
  /** Envelope paper color (hex). Defaults to ivory. */
  envelopeColor?: string | null;
  /** Wax color of the seal (hex). Defaults to the accent color. */
  sealColor?: string | null;
  /**
   * Line written on the letter that slides out as the envelope opens — the
   * campaign's own wording, or the event name when it has none.
   */
  message?: string;
  /** Caption under the envelope prompting the guest to open it. */
  hint?: string;
  /** Skip the animation entirely (already-opened links, reduced motion). */
  disabled?: boolean;
}

/**
 * Ceremonial opener for a public invitation: a fully closed envelope stamped
 * with a wax seal that the guest taps to break, after which the letter slides
 * out and the invitation is revealed. Purely presentational and
 * content-agnostic — anything can be wrapped in it — and it never traps the
 * guest: reduced-motion visitors and `disabled` skip straight to the content,
 * and the envelope is a real button so keyboard and screen-reader users open
 * it the same way.
 */
export function EnvelopeReveal({
  children,
  sealImageUrl,
  accentColor,
  envelopeColor,
  sealColor,
  message,
  hint = 'Tap the seal to open your invitation',
  disabled = false,
}: EnvelopeRevealProps) {
  const skip = disabled || prefersReducedMotion();
  const [opening, setOpening] = useState(false);
  const [revealed, setRevealed] = useState(skip);
  const timerRef = useRef<ReturnType<typeof setTimeout> | null>(null);

  useEffect(() => {
    if (skip) setRevealed(true);
  }, [skip]);

  useEffect(() => {
    return () => {
      if (timerRef.current) clearTimeout(timerRef.current);
    };
  }, []);

  const handleOpen = () => {
    if (opening || revealed) return;
    setOpening(true);
    timerRef.current = setTimeout(() => setRevealed(true), OPEN_ANIMATION_MS);
  };

  if (revealed) {
    return <div className={skip ? undefined : 'invite-reveal'}>{children}</div>;
  }

  // Paper, fold and flap shades are derived from the one chosen color so any
  // envelope color keeps the same lit-from-above look.
  const paper = isHexColor(envelopeColor) ? envelopeColor : DEFAULT_ENVELOPE_COLOR;
  const wax = isHexColor(sealColor) ? sealColor : accentColor;

  return (
    // Fills the viewport so the envelope is the whole first impression,
    // centred rather than tucked under the top edge.
    <div className="min-h-[85vh] flex flex-col items-center justify-center px-2">
      <div
        className="invite-envelope-stage w-full max-w-xl"
        style={
          {
            '--invite-accent': accentColor,
            '--invite-seal': wax,
            '--invite-envelope': paper,
            '--invite-envelope-shade': shadeHexColor(paper, -8),
            '--invite-envelope-flap': shadeHexColor(paper, 4),
          } as React.CSSProperties
        }
      >
        <button
          type="button"
          onClick={handleOpen}
          aria-label="Open your invitation"
          className="invite-envelope block w-full cursor-pointer appearance-none border-0 bg-transparent p-0"
          data-open={opening ? 'true' : 'false'}
        >
          <span className="invite-envelope__back" />
          <span className="invite-envelope__letter">
            {message && (
              <span
                className="font-serif text-base sm:text-xl leading-snug"
                style={{ color: accentColor }}
              >
                {message}
              </span>
            )}
          </span>
          <span className="invite-envelope__front" />
          <span className="invite-envelope__flap" />
          <span className="invite-envelope__seal">
            {sealImageUrl ? (
              // The logo is stamped straight into the wax — any background
              // baked into the file is knocked out so no rectangle shows.
              <TransparentImage
                src={sealImageUrl}
                alt=""
                aria-hidden="true"
                className="invite-envelope__seal-art"
              />
            ) : (
              <Mail className="w-1/2 h-1/2" aria-hidden="true" />
            )}
          </span>
        </button>
      </div>
      {!opening && (
        <p className="invite-envelope-hint mt-8 text-base" style={{ color: accentColor }}>
          {hint}
        </p>
      )}
    </div>
  );
}
